import {  useCallback } from 'react';
import { useAtom } from 'jotai';
import * as localState from './state';
import { CsvValidationException } from '@/components/Analysis/Idms/types';
import { BulkUploadPoisInput } from '@/__generated__/graphql';
import { useBulkUploadPoisMutation } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import * as state from '@/components/Analysis/state';
import { useSetAtom } from 'jotai';

export const useXlsxUploader = () => {
  const [bulkUploadModal, setBulkUploadModal] = useAtom(localState.bulkUploadModal);
  const [errors, setErrors] = useAtom(localState.errors);
  const [uploadInput, setUploadInput] = useAtom(localState.uploadInput);
  const [fileData, setFileData] = useAtom(localState.fileData);
  const [bulkUploadPois, { loading }] = useBulkUploadPoisMutation();
  const { showSnackBar } = useSnackBarMessage({});
  const setApplyFilters = useSetAtom(state.applyFilters);
  
  // File to base64 conversion
  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }, []);
  
  // Handle file selection
  const handleFileSelect = useCallback(async (file: File, structureId: string, poiType: string) => {
    try {
      // Validate file type
      if (!file.name.endsWith('.xlsx') && 
          file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        showSnackBar('Only XLSX files are supported', 'error');
        return;
      }
      
      // Store file data
      setFileData(file);
      
      // Read file as DataURL to get base64 string
      const base64Data = await fileToBase64(file);
      
      // Create input with base64 data
      const input: BulkUploadPoisInput = {
        structureId,
        poiType,
        csv: base64Data, // Backend still expects this field name
      };
      
      // Store input
      setUploadInput(input);
      
      // Open confirmation modal
      setBulkUploadModal('confirm');
      
      return input;
    } catch (error) {
      console.error('Error processing file:', error);
      showSnackBar('Error processing file', 'error');
    }
  }, [fileToBase64, setFileData, setUploadInput, setBulkUploadModal, showSnackBar]);
  
  // Upload the file
  const uploadXlsx = useCallback(async () => {
    if (!uploadInput) {
      showSnackBar('Could not create upload input. Please contact support', 'error');
      return;
    }
    
    try {
      await bulkUploadPois({
        variables: {
          input: uploadInput,
        },
      });
      
      // Refresh POI table
      setApplyFilters(true);
      showSnackBar('Points of interest uploaded successfully', 'success');
      setBulkUploadModal('closed');
    } catch (error) {
      console.error('Upload error:', error);
      
      const graphQLError = error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> };
      if (graphQLError.graphQLErrors?.[0]?.extensions?.code === 'CUSTOM_CSV_ERROR_CODE') {
        // Handle validation errors
        const validationError = graphQLError.graphQLErrors[0] as CsvValidationException;
        setErrors(validationError);
        
        // Set modal state based on error type
        if (validationError.issue.type === 'header') {
          setBulkUploadModal('invalid_format');
        } else if (validationError.issue.type === 'filetype') {
          setBulkUploadModal('invalid_format');
        } else {
          setBulkUploadModal('validation');
        }
      } else {
        showSnackBar('Could not upload points of interest. Please contact support', 'error');
      }
    }
  }, [uploadInput, bulkUploadPois, setApplyFilters, setBulkUploadModal, setErrors, showSnackBar]);
  
  // Open upload modal
  const openUploadModal = useCallback(() => {
    setBulkUploadModal('upload');
  }, [setBulkUploadModal]);
  
  // Close all modals
  const closeAllModals = useCallback(() => {
    setBulkUploadModal('closed');
    setErrors(undefined);
    setFileData(null);
  }, [setBulkUploadModal, setErrors, setFileData]);
  
  return {
    handleFileSelect,
    uploadXlsx,
    openUploadModal,
    closeAllModals,
    getModalState: () => bulkUploadModal,
    getErrors: () => errors,
    getFileData: () => fileData,
    isLoading: loading
  };
}; 